// WhatsApp Channel Service for frontend-react
// Uses Conversations API for WhatsApp channel connection

import { CONVERSATIONS_API_URL, META_INTEGRATION_ENABLED, getConversationsAuthToken } from '@/config/env'

export interface WhatsAppConnectionStatus {
  connected: boolean
  phone_number_id?: string
  account_id?: string
  metadata?: {
    phone_number_id?: string
    display_phone_number?: string
    verified_name?: string
    code_verification_status?: string
    phone_registered?: boolean
    phone_registration_status?: 'pending' | 'permission_pending' | 'registered' | 'failed'
    phone_registration_blocker?: 'business_management_advanced_access' | string
    phone_registration_error?: string | null
    phone_registration_queued_at?: string
    webhook_subscribed?: boolean
    webhook_subscription_status?: 'subscribed' | 'failed'
    webhook_subscription_error?: string | null
    webhook_subscription_checked_at?: string
    [key: string]: unknown
  }
}

export interface WhatsAppNumber {
  id: string
  display_name: string
  phone_number_id: string
  display_phone_number: string
  account_id: string
  connected: boolean
  is_default: boolean
  /** Solo lo atienden los comerciales del censo; un desconocido no da de alta nada. */
  is_internal: boolean
}

export interface WhatsAppConnectData {
  access_token: string
  phone_number_id?: string
  business_account_id?: string
}

export interface WABA {
  id: string
  name: string
  currency?: string
  message_template_namespace?: string
  phone_number_id?: string
  display_phone_number?: string
  source?: string
}

export interface TemplateButton {
  type: string
  text?: string
  /** Solo en los de tipo URL; un `{{1}}` dentro la hace dinámica. */
  url?: string
}

export interface TemplateComponent {
  type: string
  format?: string
  text?: string
  buttons?: TemplateButton[]
  example?: {
    body_text?: string[][]
    header_text?: string[]
  }
}

export interface WhatsAppTemplate {
  name: string
  status: string
  language: string
  category: string
  components: TemplateComponent[]
}

export interface TemplateParameter {
  type: 'text' | 'currency' | 'date_time' | 'image' | 'document' | 'video'
  text?: string
  [key: string]: unknown
}

export interface TemplateComponents {
  body?: TemplateParameter[]
  header?: TemplateParameter[]
  footer?: TemplateParameter[]
}

export interface SendTemplateResult {
  success: boolean
  message_id?: string
  wa_id?: string
  phone_number?: string
  error?: string
}

export class WhatsAppServiceError extends Error {
  public status?: number

  constructor(message: string, status?: number) {
    super(message)
    this.status = status
    this.name = 'WhatsAppServiceError'
  }
}

class WhatsAppServiceClient {
  private baseUrl: string

  constructor() {
    this.baseUrl = CONVERSATIONS_API_URL || ''
  }

  private getAuthToken(): string {
    return getConversationsAuthToken()
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    if (!META_INTEGRATION_ENABLED) {
      throw new WhatsAppServiceError('Meta integration is disabled in this environment', 503)
    }

    const url = `${this.baseUrl}${endpoint}`

    const config: RequestInit = {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options
    }

    // Add authentication token
    const token = this.getAuthToken()
    if (token) {
      config.headers = {
        ...config.headers,
        'Authorization': `Token ${token}`,  // Use Token auth for user-specific endpoints
      }
    }

    try {
      const response = await fetch(url, config)

      if (!response.ok) {
        const errorText = await response.text()
        throw new WhatsAppServiceError(
          `HTTP ${response.status}: ${errorText || response.statusText}`,
          response.status
        )
      }

      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json()
        return data
      } else {
        const text = await response.text()
        return text as T
      }
    } catch (error) {
      if (error instanceof WhatsAppServiceError) {
        throw error
      }
      throw new WhatsAppServiceError(`Network error: ${error}`, 0)
    }
  }

  /**
   * Connect WhatsApp channel
   */
  async connect(data: WhatsAppConnectData): Promise<WhatsAppConnectionStatus> {
    return this.request<WhatsAppConnectionStatus>('/api/v1/auth/whatsapp/connect/', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Disconnect WhatsApp channel
   */
  async disconnect(): Promise<void> {
    return this.request<void>('/api/v1/auth/whatsapp/disconnect/', {
      method: 'POST'
    })
  }

  /**
   * Get WhatsApp connection status
   */
  async getStatus(): Promise<WhatsAppConnectionStatus> {
    const response = await this.request<WhatsAppConnectionStatus>('/api/v1/auth/whatsapp/status/')
    return response
  }

  /**
   * Every WhatsApp number of this user, by name. A client may hold several.
   */
  async getConnections(): Promise<WhatsAppNumber[]> {
    const response = await this.request<{ connections: WhatsAppNumber[] }>(
      '/api/v1/auth/whatsapp/connections/'
    )
    return response.connections ?? []
  }

  /**
   * Rename a number, or hand it the traffic that names no number.
   */
  async updateConnection(
    id: string,
    changes: { display_name?: string; is_default?: boolean; is_internal?: boolean }
  ): Promise<WhatsAppNumber> {
    return this.request<WhatsAppNumber>('/api/v1/auth/whatsapp/connections/', {
      method: 'PATCH',
      body: JSON.stringify({ id, ...changes }),
    })
  }

  /**
   * Re-queue WhatsApp Cloud API phone registration (Celery task; uses server System User token).
   */
  async retryRegistration(body?: {
    registration_pin?: string
    certificate?: string
  }): Promise<{ registration_status: string; registration_message?: string }> {
    return this.request<{ registration_status: string; registration_message?: string }>(
      '/api/v1/auth/whatsapp/retry-registration/',
      {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      }
    )
  }

  /**
   * Get available WhatsApp Business Accounts (WABAs)
   */
  async getWabas(): Promise<WABA[]> {
    interface WABAResponse {
      data: Array<{
        owned_whatsapp_business_accounts?: {
          data: WABA[]
        }
      }>
    }

    const response = await this.request<WABAResponse>('/api/v1/leads/meta/waba/')
    const businesses = response.data || []
    const allWabas: WABA[] = []

    businesses.forEach((biz) => {
      if (biz.owned_whatsapp_business_accounts?.data) {
        allWabas.push(...biz.owned_whatsapp_business_accounts.data)
      }
    })

    return allWabas
  }

  /**
   * Get templates for a specific WABA
   */
  async getTemplates(wabaId: string): Promise<WhatsAppTemplate[]> {
    interface TemplatesResponse {
      data: WhatsAppTemplate[]
    }

    const response = await this.request<TemplatesResponse>(`/api/v1/leads/meta/templates/?waba_id=${wabaId}`)
    return response.data || []
  }

  /**
   * Create a new WhatsApp message template
   */
  async createTemplate(
    wabaId: string,
    name: string,
    language: string,
    category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION',
    bodyText: string,
    exampleBodyText?: string[][]
  ): Promise<{ id: string; name: string; status: string; language: string }> {
    const body: {
      waba_id: string
      name: string
      language: string
      category: string
      body_text: string
      example_body_text?: string[][]
    } = {
      waba_id: wabaId,
      name,
      language,
      category,
      body_text: bodyText,
    }

    if (exampleBodyText && exampleBodyText.length > 0) {
      body.example_body_text = exampleBodyText
    }

    return this.request<{ id: string; name: string; status: string; language: string }>(
      '/api/v1/leads/meta/templates/',
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    )
  }

  /**
   * Send a template message to a lead
   */
  async sendTemplateToLead(
    leadId: string,
    templateName: string,
    languageCode: string = 'es_ES',
    components?: TemplateComponents,
    templateBodyText?: string
  ): Promise<SendTemplateResult> {
    const body: {
      template_name: string
      language_code: string
      components?: TemplateComponents
      template_body_text?: string
    } = {
      template_name: templateName,
      language_code: languageCode,
    }

    if (components) {
      body.components = components
    }

    if (templateBodyText) {
      body.template_body_text = templateBodyText
    }

    return this.request<SendTemplateResult>(`/api/v1/whatsapp/send-template-to-lead/${leadId}/`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }
}

// Export singleton instance
export const whatsappService = new WhatsAppServiceClient()
